/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import _ from 'lodash';
import { useState } from 'react';
import { saveAs } from 'file-saver';
import type { CellObject, ColInfo, Range, RowInfo, WorkBook, WorkSheet } from 'xlsx-js-style';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getTextFromHTML,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import Annotations, { AnnotationFilters } from '../Annotations/Annotations';

import { processedData } from './processedData';
import tableStyles, { distributionsTableThemeStyles } from './DistributionsTable.styles';

export interface SheetTabOption {
    name: string;
    data: string;
    showFundName: boolean;
    showDisclaimer: boolean;
}

export interface TableColumn {
    id: string;
    label?: string;
    excelLabel?: string;
    value: string;
    groupValue: string;
    filterBy: string;
    groupBy: string;
}

export interface ScheduleOption {
    label?: string;
    excelLabel?: string;
    value: string;
}

export interface TableOptions {
    columns: TableColumn[];
    tableDatakey?: string;
    iconShowContent?: string;
    iconHideContent?: string;
    downloadButtonName?: string;
    downloadFileName?: string;
    titles?: Headings[];
    scheduleTitles?: Headings[];
    scheduleOptions?: ScheduleOption[];
    transformData?: boolean;
    functionToTransformData?: string;
    fileType?: string;
    excelTabs: SheetTabOption[];
    csvOptions: SheetTabOption;
    disclaimer: { filterBy: string; filters: AnnotationFilters };
    typographyClass?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, TableOptions {}

const DistributionsTable = (props: commonTypes.WidgetInstanceProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, columns, tableDatakey, ...rest } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    const [isExpanded, setIsExpanded] = useState<any>({});
    const [disclaimer, setDisclaimer] = useState<string>('');
    const tableData = tableDatakey ? JsonQuery(tableDatakey as string, { data }).value : null;
    const filterId = _.find(columns, (c) => !!c.filterBy)?.id || '';
    const rows = tableData?.length && tableData?.filter((t: any) => t[filterId]);

    let isRender: any = {
        scheduleOptions: rest.scheduleOptions,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const _transformedData =
            rest.transformData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformData,
                {
                    data,
                    ...isRender,
                }
            );

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const { rowGroups }: any =
        columns?.length > 0 && rows?.length > 0 && processedData(rows, columns);

    rowGroups?.sort((a: number, b: number) => a - b)?.reverse();

    const onDownloadFile = () => {
        // use XLSX here
        const fileType: string =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const firstColumnTitle: string = `${JsonQuery('overview.fundName', { data }).value} ${
            JsonQuery('overview.seriesName', { data }).value
        }`;
        let csvColumns: TableColumn[] = [...columns];
        // Initialize workbook
        import('xlsx-js-style').then(({ utils, write }) => {
            const wb: WorkBook = utils?.book_new();
            const tabs: SheetTabOption[] =
                rest.fileType === '.xlsx' ? rest?.excelTabs : [rest?.csvOptions];

            // process each tabs options
            tabs?.forEach((opt: SheetTabOption) => {
                const rowsData: any = []; // worksheet data
                const wsCols: ColInfo[] = []; // worksheet column options
                const wsMerges: Range[] = []; // worksheet merges options
                const wsRows: RowInfo[] = []; // worksheet row options

                // check which data to show for worksheet
                if (opt?.data === 'Schedule Options') {
                    // add first coulun to worksheet
                    if (opt?.showFundName) {
                        const firstColumn: TableColumn = {
                            id: '',
                            excelLabel: firstColumnTitle,
                            value: '',
                            groupValue: '',
                            filterBy: '',
                            groupBy: '',
                        };
                        isRender.scheduleOptions = [firstColumn, ...isRender.scheduleOptions];
                    }

                    const row: any = {};
                    isRender.scheduleOptions?.forEach((s: ScheduleOption, index: number) => {
                        const label = i18n.format(s.excelLabel || '', data) || '';
                        const value = i18n.format(s.value || '', data);
                        row[label] = value;
                        wsCols[index] = {
                            wch: label.length >= value.length ? label.length : value.length,
                        };
                    });
                    // add rows to worksheet
                    rowsData.push(row);
                } else {
                    // add first coulun to worksheet
                    if (opt?.showFundName) {
                        const firstColumn: TableColumn = {
                            id: '',
                            excelLabel: firstColumnTitle,
                            value: '',
                            groupValue: '',
                            filterBy: '',
                            groupBy: '',
                        };
                        csvColumns = [firstColumn, ...csvColumns];
                    }

                    const combinedRows =
                        rowGroups?.length && rowGroups.map((r: any) => r.rows).flat(1);
                    combinedRows?.forEach((r: any) => {
                        const row: any = {};
                        csvColumns?.forEach((c: TableColumn, index: number) => {
                            const label = i18n.format(c.excelLabel || '', data) || '';
                            const value = i18n.format(c.value || '', { ...r, ...data });
                            row[label] = value;
                            wsCols[index] = {
                                wch: label.length >= value.length ? label.length : value.length,
                            };
                        });
                        // add rows to worksheet
                        rowsData.push(row);
                    });
                }

                // check if disclaimer should show or not
                if (opt?.showDisclaimer) {
                    // add disclaimer to worksheet
                    rowsData[rowsData.length + 1] = { [firstColumnTitle]: disclaimer };
                    // merge worksheet cells for disclaimer
                    const merge: Range = {
                        s: { r: rowsData.length, c: 0 },
                        e: { r: rowsData.length, c: csvColumns.length - 1 },
                    };
                    // calculate row height for disclaimer
                    const height = disclaimer.toString().trim().length;
                    wsRows[rowsData.length] = {
                        hpx: height / (csvColumns.length + 1),
                    };
                    wsMerges.push(merge);
                }

                const ws: WorkSheet = utils?.json_to_sheet(rowsData);
                if (ws) {
                    ws['!cols'] = wsCols;
                    ws['!merges'] = wsMerges;
                    ws['!rows'] = wsRows;

                    // apply style to worksheet
                    for (const i in ws as CellObject) {
                        if (typeof ws[i] === 'object' && ws[i].v === disclaimer) {
                            ws[i].s = {
                                alignment: {
                                    wrapText: true,
                                },
                            };
                        }
                    }
                }
                // append each sheet to workbook
                utils?.book_append_sheet(wb, ws, `${i18n.format(opt?.name, data)}`);
            });
            let excelBuffer: string = '';
            if (wb) {
                excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
            }
            // check file type for worksheet
            if (rest?.fileType === '.xlsx') {
                const _data = new Blob([excelBuffer], { type: fileType });
                saveAs(
                    _data,
                    `${i18n.format(rest.downloadFileName || '', data)}.xlsx` || 'download.xlsx'
                );
            } else if (rest?.fileType === '.csv') {
                // read xlsx file
                import('xlsx').then(({ read }) => {
                    // use XLSX here
                    const rs = read(excelBuffer, { type: 'file' });
                    // generate csv file
                    if (rs) {
                        const csvBuffer: string = write(rs, { bookType: 'csv', type: 'array' });
                        const blobCSV = new Blob([csvBuffer], { type: 'text/plain;charset=UTF-8' });
                        saveAs(
                            blobCSV,
                            `${i18n.format(rest.downloadFileName || '', data)}.csv` ||
                                'download.csv'
                        );
                    }
                });
            }
        });
    };

    return (
        <div
            className={widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''}
            css={css`
                ${!theme ? tableStyles : distributionsTableThemeStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
        >
            <button
                className="download-button"
                data-testid="download-button"
                onClick={() => onDownloadFile()}
            >
                <span
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(rest.downloadButtonName || '', data),
                    }}
                />
            </button>
            <div className="distributions--schedule">
                <div className="schedule--title">
                    <HeaderWrapper headings={rest?.scheduleTitles} data={data} />
                </div>
                {isRender.scheduleOptions?.map((s: ScheduleOption, i: number) => (
                    <div className="schedule--row" key={i}>
                        <label
                            className="schedule--label"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(s.label || '', data),
                            }}
                        />
                        <span
                            className="schedule--value"
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(s.value || '', data),
                            }}
                        />
                    </div>
                ))}
            </div>
            <div className="table-title">
                <HeaderWrapper headings={rest?.titles} data={data} />
            </div>
            <div className="table-wrapper">
                <table
                    className={'table table--distributions'}
                    role="table"
                    aria-rowcount={rowGroups?.length || 0}
                >
                    <thead>
                        <tr className="header-row">
                            {columns?.map((c: TableColumn, index: number) => {
                                return (
                                    <th key={index}>
                                        <div
                                            key={index}
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(c.label || '', data) || '',
                                            }}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData?.length &&
                            rowGroups?.map((rowGroup: any, index: number) => {
                                return (
                                    index <= 10 && (
                                        <>
                                            <tr
                                                key={index}
                                                data-group-id={rowGroup.id}
                                                className="row--group"
                                            >
                                                {columns?.map((c: TableColumn, id: number) => (
                                                    <td>
                                                        {id === 0 && (
                                                            <span
                                                                className="icon-button row-group-toggle"
                                                                onClick={() =>
                                                                    setIsExpanded({
                                                                        ...isExpanded,
                                                                        [index]: !isExpanded[index],
                                                                    })
                                                                }
                                                            >
                                                                <i
                                                                    className={
                                                                        isExpanded[index]
                                                                            ? rest.iconHideContent
                                                                            : rest.iconShowContent
                                                                    }
                                                                />
                                                            </span>
                                                        )}
                                                        <span
                                                            className="table--text"
                                                            key={`${c.value}${index}${id}`}
                                                            dangerouslySetInnerHTML={{
                                                                __html: i18n.format(
                                                                    c.groupValue || '',
                                                                    { ...rowGroup, ...data }
                                                                ),
                                                            }}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                            {isExpanded[index] &&
                                                rowGroup?.rows?.map((row: any, index: number) => {
                                                    return (
                                                        <tr key={index} className={'table--row'}>
                                                            {columns?.map(
                                                                (c: TableColumn, id: number) => (
                                                                    <td
                                                                        key={`${c.value}${index}${id}`}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: i18n.format(
                                                                                c.value || '',
                                                                                { ...row, ...data }
                                                                            ),
                                                                        }}
                                                                    />
                                                                )
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                        </>
                                    )
                                );
                            })}
                    </tbody>
                </table>
            </div>
            <div className="annotations">
                <Annotations
                    filterBy={rest.disclaimer?.filterBy}
                    filters={rest.disclaimer?.filters}
                    data={data}
                    setDiscalimer={(d) =>
                        setDisclaimer(
                            getTextFromHTML(d.replace(/<sup>[\s\S]*<\/sup>/g, ''))?.trim() || ''
                        )
                    }
                />
            </div>
        </div>
    );
};

export default DistributionsTable;
